import { createApi } from '@reduxjs/toolkit/query/react';
import { ENDPOINT_CONSTANTS } from '../../constant';
import baseQueryWithReauth from 'service/baseQuery/baseQuery';
import { StatsProp } from 'pages/institutionDashboard/stats/stats.type';

type ApiResponse<T> = {
  data: T;
  status: number;
  message?: string;
};

export const institutionQuery = createApi({
  reducerPath: 'institutionQuery',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['INS_INDIVIDUAL_CANDIDATE', 'INS_CANDIDATE'],
  endpoints: builder => ({
    createCandidate: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['INS_CANDIDATE']
    }),
    findCandidate: builder.query({
      query: params => {
        let url = ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE;
        if (typeof params === 'object') {
          url += `?${new URLSearchParams(params).toString()}`;
        }
        return {
          url,
          method: 'GET'
        };
      },
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      },
      providesTags: ['INS_CANDIDATE']
    }),
    findCandidateRegisterNumber: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.INSITITUTION_CANDIDATE_REGISTER_NUMBER,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      }
    }),
    saveBulkCandidate: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE_BULK,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['INS_CANDIDATE']
    }),
    findIndividualCandidate: builder.query({
      query: id => ({
        url: `${ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE}/${id}`,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      },
      providesTags: ['INS_INDIVIDUAL_CANDIDATE']
    }),
    updateCandidate: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `${ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE}/${id}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['INS_INDIVIDUAL_CANDIDATE', 'INS_CANDIDATE']
    }),
    deleteCandidate: builder.mutation({
      query: id => ({
        url: `${ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['INS_CANDIDATE']
    }),
    getDashboard: builder.query<StatsProp, void>({
      query: () => ({
        url: ENDPOINT_CONSTANTS.INSTITUTION_DASHBOARD,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<StatsProp>): StatsProp => {
        return response.data;
      }
    }),

    studentRegister: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.INSTITUTION_CANDIDATE_SELF}/${id}`,
        method: 'POST',
        body
      })
    }),
    getDepartmentAvg: builder.query({
      query: date => ({
        url: `${ENDPOINT_CONSTANTS.INSTITUTION_DEPARTMENT}?date=${date}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useCreateCandidateMutation,
  useFindCandidateQuery,
  useLazyGetDepartmentAvgQuery,
  useGetDashboardQuery,
  useFindCandidateRegisterNumberQuery,
  useSaveBulkCandidateMutation,
  useFindIndividualCandidateQuery,
  useUpdateCandidateMutation,
  useDeleteCandidateMutation,
  useStudentRegisterMutation,
  useLazyFindCandidateQuery
} = institutionQuery;
